import { z } from 'zod';
import { motDePasseSchema } from './auth.js';
import { AXES, poidsSchema, sommePoids, type Axe, type Poids } from './bareme.js';
import type { GroupeBareme } from './familles.js';
import type { Role } from './roles.js';

/**
 * Administration du super admin (docs/UI.md § Écran 6) : comptes et rôles,
 * points de vente et familles, versions des barèmes, journal d'audit.
 */

export const LIBELLES_ROLE: Record<Role, string> = {
  super_admin: 'Super admin',
  rh: 'RH',
  lecteur: 'Lecteur',
};

export const DESCRIPTIONS_ROLE: Record<Role, string> = {
  super_admin: 'Tout, plus l’administration : comptes, référentiels, barèmes, paramètres, journal.',
  rh: 'Annonces, candidatures, contacts, entretiens, chartes, embauches, exports avec coordonnées.',
  lecteur: 'Consultation et statistiques, sans e-mail ni document ; téléphone des candidats seulement si « Voir les téléphones » est coché.',
};

// ---------------------------------------------------------------------------
// Référentiels
// ---------------------------------------------------------------------------

const texte = (max: number) => z.string().trim().min(1, 'Obligatoire').max(max, `${max} caractères au plus`);
const texteFacultatif = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `${max} caractères au plus`)
    .nullable()
    .transform((v) => (v === '' ? null : v));

/**
 * Le code d'une famille ne change pas : les chartes et les poids par défaut
 * s'y rattachent dans le code. On en règle le libellé, l'ordre et l'activité.
 */
export const familleModificationSchema = z.object({
  libelle: texte(120),
  libelleAr: texteFacultatif(120),
  ordre: z.number().int().min(0).max(10_000),
  actif: z.boolean(),
});
export type FamilleModification = z.infer<typeof familleModificationSchema>;

/** Code court en majuscules, comme ceux de l'Order Hub (MNB, DHR…) ; il ne se modifie plus ensuite. */
export const CODE_POINT_DE_VENTE = /^[A-Z0-9]{2,6}$/;

export const pointDeVenteCreationSchema = z.object({
  code: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(CODE_POINT_DE_VENTE, '2 à 6 lettres ou chiffres, sans espace')),
  libelle: texte(120),
  adresse: texteFacultatif(300),
});
export type PointDeVenteCreation = z.infer<typeof pointDeVenteCreationSchema>;

export const pointDeVenteModificationSchema = z.object({
  libelle: texte(120),
  adresse: texteFacultatif(300),
  actif: z.boolean(),
});
export type PointDeVenteModification = z.infer<typeof pointDeVenteModificationSchema>;

// ---------------------------------------------------------------------------
// Barèmes
// ---------------------------------------------------------------------------

export const TOTAL_POIDS = 100;
const TOLERANCE_TOTAL = 0.05;

const auDixieme = (valeur: number) => Math.abs(valeur * 10 - Math.round(valeur * 10)) < 1e-6;

/**
 * Nouvelle version d'un barème (docs/BAREME.md § Poids par défaut) : chaque
 * poids au dixième près, total de 100 % exactement. Les anciennes lignes à
 * 105 % restent lisibles, mais aucune nouvelle version ne s'écarte de 100.
 */
export const nouveauBaremeSchema = z.object({
  poids: poidsSchema
    .refine((poids) => AXES.every((axe) => auDixieme(poids[axe])), { message: 'Poids au dixième près' })
    .refine((poids) => Math.abs(sommePoids(poids) - TOTAL_POIDS) <= TOLERANCE_TOTAL, {
      message: `Les poids doivent totaliser ${TOTAL_POIDS} %`,
    }),
  /** Version en vigueur vue à l'écran (null : aucune) ; une autre en vigueur entre-temps → 409. */
  depuisVersion: z.number().int().positive().nullable(),
});
export type NouveauBareme = z.infer<typeof nouveauBaremeSchema>;

export function totalA100(poids: Poids): boolean {
  return Math.abs(sommePoids(poids) - TOTAL_POIDS) <= TOLERANCE_TOTAL;
}

const dixieme = (valeur: number) => Math.round(valeur * 10) / 10;

/**
 * Ramène un barème à 100 % en gardant ses proportions (cas des lignes à
 * 105 %) ; l'écart d'arrondi va à l'axe le plus lourd.
 */
export function normaliserA100(poids: Poids): Poids {
  const total = sommePoids(poids);
  if (total <= 0) return { ...poids };
  const suivant = Object.fromEntries(AXES.map((axe) => [axe, dixieme((poids[axe] * TOTAL_POIDS) / total)])) as Poids;
  const ecart = dixieme(TOTAL_POIDS - sommePoids(suivant));
  if (ecart !== 0) {
    const plusLourd = [...AXES].sort((a, b) => suivant[b] - suivant[a])[0]!;
    suivant[plusLourd] = dixieme(suivant[plusLourd] + ecart);
  }
  return suivant;
}

/**
 * Saisie d'un poids : il est posé, et seuls les axes pas encore modifiés
 * (hors `figes`) se réajustent en proportion pour viser 100 %. Les poids déjà
 * saisis ne bougent plus ; si eux seuls dépassent 100 %, le total l'affiche.
 */
export function repartirPoids(poids: Poids, axe: Axe, valeur: number, figes: readonly Axe[]): Poids {
  const suivant = { ...poids, [axe]: dixieme(Math.min(TOTAL_POIDS, Math.max(0, valeur))) } as Poids;
  const libres = AXES.filter((a) => a !== axe && !figes.includes(a));
  if (libres.length === 0) return suivant;
  const fixe = AXES.filter((a) => !libres.includes(a)).reduce((total, a) => total + suivant[a], 0);
  const resteVoulu = Math.max(0, TOTAL_POIDS - fixe);
  const resteActuel = libres.reduce((total, a) => total + poids[a], 0);
  for (const a of libres) {
    suivant[a] = dixieme(resteActuel === 0 ? resteVoulu / libres.length : (poids[a] * resteVoulu) / resteActuel);
  }
  const ecart = dixieme(TOTAL_POIDS - sommePoids(suivant));
  if (ecart !== 0 && resteVoulu > 0) {
    const plusLourd = [...libres].sort((a, b) => suivant[b] - suivant[a])[0]!;
    suivant[plusLourd] = Math.max(0, dixieme(suivant[plusLourd] + ecart));
  }
  return suivant;
}

export interface BaremeVersionResume {
  id: string;
  version: number;
  poids: Poids;
  total: number;
  actif: boolean;
  creeLe: string;
  /** null : version posée par le semis. */
  creePar: string | null;
  /** Annonces validées qui ont figé cette version. */
  annoncesFigees: number;
  evaluations: number;
}

export interface BaremeFamille {
  familleId: string;
  code: string;
  libelle: string;
  actif: boolean;
  groupe: GroupeBareme | null;
  /** Version appliquée aux annonces pas encore validées : la plus récente active. */
  enVigueur: BaremeVersionResume | null;
  /** De la plus récente à la plus ancienne. */
  versions: BaremeVersionResume[];
}

// ---------------------------------------------------------------------------
// Journal d'audit
// ---------------------------------------------------------------------------

export const CATEGORIES_JOURNAL = [
  { cle: 'connexions', libelle: 'Connexions et sessions', prefixes: ['connexion.', 'deconnexion', 'session.', 'mot_de_passe.'] },
  { cle: 'comptes', libelle: 'Comptes et rôles', prefixes: ['utilisateur.'] },
  { cle: 'reglages', libelle: 'Paramètres, référentiels et barèmes', prefixes: ['parametre.', 'famille.', 'point_de_vente.', 'bareme.'] },
  { cle: 'annonces', libelle: 'Annonces', prefixes: ['annonce.', 'lien.'] },
  { cle: 'candidatures', libelle: 'Candidatures, CV et ingestion', prefixes: ['candidature.', 'cv.', 'ingestion.'] },
  { cle: 'entretiens', libelle: 'Contacts, entretiens et notes', prefixes: ['contact.', 'evaluation.', 'note.'] },
  { cle: 'dossiers', libelle: 'Chartes, rapports et justificatifs', prefixes: ['charte.', 'rapport.', 'justificatif'] },
  { cle: 'employes', libelle: 'Employés', prefixes: ['employe.'] },
  { cle: 'donnees', libelle: 'Données personnelles et purges', prefixes: ['personne.', 'purge.'] },
  { cle: 'exports', libelle: 'Exports Excel', prefixes: ['export.'] },
] as const;
export type CategorieJournal = (typeof CATEGORIES_JOURNAL)[number]['cle'];

export function categorieDe(action: string): CategorieJournal | null {
  return CATEGORIES_JOURNAL.find((c) => c.prefixes.some((p) => action.startsWith(p)))?.cle ?? null;
}

export const LIBELLES_ACTION: Record<string, string> = {
  'connexion.reussie': 'Connexion',
  'connexion.refusee': 'Connexion refusée',
  deconnexion: 'Déconnexion',
  'session.reprise_rotation': 'Session reprise après une réponse perdue',
  'mot_de_passe.change': 'Mot de passe changé par son titulaire',
  'mot_de_passe.refuse': 'Changement de mot de passe refusé (mot de passe actuel faux)',
  'utilisateur.cree': 'Compte créé',
  'utilisateur.modifie': 'Compte modifié',
  'utilisateur.apparence': 'Style d’affichage changé par son titulaire',
  'parametre.modifie': 'Paramètre modifié',
  'personne.telephone_cv': 'Téléphone repris du CV joint',
  'famille.modifiee': 'Métier modifié',
  'point_de_vente.cree': 'Point de vente créé',
  'point_de_vente.modifie': 'Point de vente modifié',
  'bareme.version_creee': 'Nouvelle version de barème',
  'annonce.creee': 'Annonce créée',
  'annonce.enregistree': 'Annonce enregistrée',
  'annonce.validee': 'Annonce validée',
  'annonce.close': 'Annonce close',
  'annonce.publication_declaree': 'Publication déclarée',
  'annonce.publication_modifiee': 'Publication modifiée',
  'annonce.rs_creee': 'Annonce réseaux sociaux créée',
  'lien.cree': 'Lien de formulaire créé',
  'lien.desactive': 'Lien de formulaire désactivé',
  'lien.regenere': 'Lien de formulaire régénéré',
  'candidature.whatsapp': 'Candidature saisie depuis WhatsApp',
  'candidature.statut': 'Statut de candidature changé',
  'candidature.rattachee': 'Candidature rattachée depuis le vivier',
  'ingestion.rattachee': 'Message rattaché à une annonce',
  'ingestion.relancee': 'Lecture de la boîte relancée',
  'cv.depose': 'CV déposé',
  'cv.rattache': 'CV rattaché',
  'cv.ecarte': 'CV écarté',
  'cv.retire': 'CV retiré d’une fiche',
  'contact.ouvert': 'Contact ouvert',
  'contact.modifie': 'Contact modifié',
  'contact.cloture': 'Contact clôturé',
  'evaluation.creee': 'Entretien noté',
  'note.forcee': 'Note forcée',
  'charte.editee': 'Charte éditée',
  'charte.signee_enregistree': 'Charte signée enregistrée',
  'rapport.edite': 'Rapport de référence édité',
  'rapport.signe_enregistre': 'Rapport signé enregistré',
  'justificatifs.definis': 'Justificatifs demandés',
  'justificatif.recu': 'Justificatif reçu',
  'employe.cree': 'Embauche',
  'employe.modifie': 'Fiche employé modifiée',
  'employe.sortie': 'Sortie d’un employé',
  'personne.ne_plus_contacter': '« Ne plus contacter » modifié',
  'personne.coordonnees_completees': 'Coordonnées complétées depuis un CV',
  'personne.conservation_prolongee': 'Conservation prolongée',
  'personne.supprimee': 'Fiche supprimée définitivement',
  'purge.anonymisation': 'Anonymisation automatique (conservation)',
  'cv.expires': 'CV non rattachés effacés automatiquement',
  'export.candidatures': 'Export Excel des candidatures',
  'export.employes': 'Export Excel des employés',
  'export.statistiques': 'Export Excel des statistiques',
  'export.comparateur': 'Export Excel d’une comparaison',
};

export function libelleAction(action: string): string {
  return LIBELLES_ACTION[action] ?? action;
}

/** Écran d'un élément du journal, s'il en a un. */
export function lienEntite(entite: string, entiteId: string | null): string | null {
  if (!entiteId) return null;
  if (entite === 'candidature') return `/candidatures/${entiteId}`;
  if (entite === 'employe') return `/employes/${entiteId}`;
  if (entite === 'annonce') return `/annonces/${entiteId}`;
  return null;
}

export const JOURNAL_LIMITE_DEFAUT = 50;
export const JOURNAL_LIMITE_MAX = 200;
const jour = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
  .refine((v) => {
    const date = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === v;
  }, 'Date inexistante');

export const journalFiltresSchema = z
  .object({
    categorie: z.enum(CATEGORIES_JOURNAL.map((c) => c.cle) as [CategorieJournal, ...CategorieJournal[]]).optional(),
    utilisateur: z.string().uuid('Compte invalide').optional(),
    du: jour.optional(),
    au: jour.optional(),
    /** Curseur : identifiant de la dernière entrée déjà affichée. */
    // 18 chiffres : toujours sous le maximum d'un bigint PostgreSQL.
    avant: z.string().regex(/^\d{1,18}$/, 'Curseur invalide').optional(),
    limite: z.coerce.number().int().min(1).max(JOURNAL_LIMITE_MAX).default(JOURNAL_LIMITE_DEFAUT),
  })
  .refine((f) => !f.du || !f.au || f.du <= f.au, { message: 'La date de début suit la date de fin', path: ['du'] });
export type JournalFiltres = z.infer<typeof journalFiltresSchema>;

export interface JournalEntree {
  id: string;
  horodatage: string;
  /** null : action du système (purge nocturne, ingestion). */
  utilisateur: { id: string; nom: string; email: string } | null;
  action: string;
  entite: string;
  entiteId: string | null;
  avant: unknown;
  apres: unknown;
}

export interface PageJournal {
  entrees: JournalEntree[];
  /** Curseur de la page suivante, null en fin de journal. */
  suivant: string | null;
}

// ---------------------------------------------------------------------------
// Mon compte
// ---------------------------------------------------------------------------

export const changementMotDePasseSchema = z.object({
  actuel: z.string().min(1, 'Obligatoire').max(200),
  nouveau: motDePasseSchema,
});
export type ChangementMotDePasse = z.infer<typeof changementMotDePasseSchema>;
